import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ENC } from "@/constants/testIds";

const ORDER_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "medication", label: "Medications" },
  { id: "lab", label: "Labs" },
  { id: "imaging", label: "Imaging" },
  { id: "referral", label: "Referrals" },
  { id: "procedure", label: "Procedures" },
];

/**
 * Placeholder catalog for the UI shell only. Orders have no backend yet —
 * see WhatToOrderPanel — so this list, and everything selected from it, is
 * local component state that is never persisted or sent anywhere.
 */
const ORDER_CATALOG = [
  { id: "lab-cbc", category: "lab", label: "CBC with Differential" },
  { id: "lab-cmp", category: "lab", label: "Comprehensive Metabolic Panel" },
  { id: "img-xray-lspine", category: "imaging", label: "X-ray, Lumbar Spine" },
  { id: "img-mri-lspine", category: "imaging", label: "MRI, Lumbar Spine w/o Contrast" },
  { id: "med-naproxen", category: "medication", label: "Naproxen 500mg tablet" },
  { id: "ref-pt", category: "referral", label: "Physical Therapy Referral" },
  { id: "proc-epidural", category: "procedure", label: "Epidural Steroid Injection" },
];

/**
 * UI shell for the Order tab of the right-side coding panel. There is no
 * order-entry backend yet (see WhatToOrderPanel), so search, selection, and
 * instructions are all local state — nothing here is persisted to the
 * Encounter or sent to the server.
 */
export default function EncounterOrderPanel({ readOnly }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState([]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ORDER_CATALOG.filter((item) => {
      if (category !== "all" && item.category !== category) return false;
      if (q && !item.label.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [query, category]);

  const selectedIds = new Set(selected.map((row) => row.id));

  const addOrder = (item) => {
    if (readOnly || selectedIds.has(item.id)) return;
    setSelected((current) => [...current, { ...item, instructions: "" }]);
  };

  const removeOrder = (id) => {
    if (readOnly) return;
    setSelected((current) => current.filter((row) => row.id !== id));
  };

  const setInstructions = (id, instructions) => {
    if (readOnly) return;
    setSelected((current) => current.map((row) => (row.id === id ? { ...row, instructions } : row)));
  };

  return (
    <div data-testid={ENC.screenOrderPanel} className="space-y-3">
      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-2.5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-900">Orders</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            UI shell only — order entry is not implemented yet. Medications, labs, imaging,
            referrals, and procedures will attach to the encounter here.
          </p>
        </div>

        <div className="space-y-3 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              type="search"
              autoComplete="off"
              spellCheck={false}
              data-testid={ENC.screenOrderSearchInput}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search orders…"
              className="h-10 bg-white pl-10 text-sm"
              disabled={readOnly}
            />
          </div>

          <div data-testid={ENC.screenOrderCategories} className="flex flex-wrap gap-1.5">
            {ORDER_CATEGORIES.map((cat) => {
              const active = category === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  data-testid={`${ENC.screenOrderCategory}-${cat.id}`}
                  aria-pressed={active}
                  onClick={() => setCategory(cat.id)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
                    active
                      ? "bg-[#0b1f33] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {!results.length ? (
            <p data-testid={ENC.screenOrderSearchEmpty} className="py-6 text-center text-sm text-slate-500">
              No orders match this search.
            </p>
          ) : (
            <ul
              data-testid={ENC.screenOrderResults}
              className="divide-y divide-slate-100 rounded-md border border-slate-100"
            >
              {results.map((item) => {
                const added = selectedIds.has(item.id);
                return (
                  <li key={item.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">{item.label}</span>
                    {added ? (
                      <span
                        data-testid={`${ENC.screenOrderResult}-${item.id}-added`}
                        className="w-14 shrink-0 text-right text-xs font-semibold text-emerald-700"
                      >
                        Added
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`${ENC.screenOrderResult}-${item.id}`}
                        disabled={readOnly}
                        onClick={() => addOrder(item)}
                        className="w-14 shrink-0 rounded-md bg-sky-50 py-0.5 text-center text-xs font-semibold text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Add
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section
        data-testid={ENC.screenSelectedOrders}
        className="overflow-hidden rounded-lg border border-slate-200 bg-white"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900">Selected Orders</h3>
          <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{selected.length}</Badge>
        </div>

        {!selected.length ? (
          <p className="px-4 py-6 text-center text-sm text-slate-500">
            No orders are selected for this encounter yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {selected.map((row) => (
              <li
                key={row.id}
                data-testid={`${ENC.screenSelectedOrderRow}-${row.id}`}
                className="space-y-1.5 px-4 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">{row.label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-rose-600"
                    disabled={readOnly}
                    aria-label={`Remove ${row.label}`}
                    data-testid={`${ENC.screenOrderRemove}-${row.id}`}
                    onClick={() => removeOrder(row.id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <input
                  type="text"
                  data-testid={`${ENC.screenOrderInstructions}-${row.id}`}
                  value={row.instructions}
                  disabled={readOnly}
                  onChange={(event) => setInstructions(row.id, event.target.value)}
                  placeholder="Instructions / details…"
                  className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
